import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useData } from '../contexts/DataContext';

const GallerySection = () => {
  const { properties, blogPosts, heroSlides, galleryImages, galleryCategories, loading } = useData();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const galleryCategoryOptions = useMemo(() => {
    const names = new Set();
    (galleryCategories || []).forEach((category) => {
      if (category?.name) names.add(category.name);
    });
    return Array.from(names);
  }, [galleryCategories]);

  const featuredImages = useMemo(() => {
    const MAX_ITEMS = 6;
    const items = [];
    const pushImage = (url, meta) => {
      if (!url || items.length >= MAX_ITEMS) return;
      items.push({ url, ...meta });
    };

    const resolveGalleryCategory = (record) => {
      if (record?.category_name) return record.category_name;
      const match = (galleryCategories || []).find((cat) => cat.id === record?.category_id);
      return match?.name || 'Gallery';
    };

    const normalizeGalleryRecords = () => {
      const featured = [];
      const regular = [];
      (galleryImages || []).forEach((record) => {
        const recordImages = Array.isArray(record.images) && record.images.length > 0
          ? record.images
          : (record.image_url ? [record.image_url] : []);

        recordImages.forEach((imageUrl, imageIndex) => {
          const normalized = {
            url: imageUrl,
            type: 'gallery',
            title: record.title || record.category_name || 'Featured Gallery',
            subtitle: record.description || record.category_name || 'Gallery Showcase',
            displayOrder: Number(record.display_order) || 0,
            imageIndex,
            category: resolveGalleryCategory(record),
          };
          if (record.is_featured) {
            featured.push(normalized);
          } else {
            regular.push(normalized);
          }
        });
      });

      const sorter = (a, b) => {
        if (a.displayOrder !== b.displayOrder) return a.displayOrder - b.displayOrder;
        return a.imageIndex - b.imageIndex;
      };

      return {
        featured: featured.sort(sorter),
        regular: regular.sort(sorter),
      };
    };

    const { featured: featuredGallery, regular: fallbackGallery } = normalizeGalleryRecords();

    const filterByCategory = (list) => {
      if (selectedCategory === 'all') return list;
      return list.filter(
        (image) => image.category && image.category.toLowerCase() === selectedCategory.toLowerCase()
      );
    };

    [...filterByCategory(featuredGallery), ...filterByCategory(fallbackGallery)].forEach((image) =>
      pushImage(image.url, image)
    );

    if (selectedCategory === 'all' && items.length < MAX_ITEMS && heroSlides) {
      heroSlides.forEach((slide) => {
        if (items.length >= MAX_ITEMS) return;
        if (slide.background_image) {
          pushImage(slide.background_image, {
            type: 'hero',
            title: slide.title || 'Hero Showcase',
            subtitle: slide.subtitle || 'Featured Development',
            category: 'Hero Slides',
          });
        }
      });
    }

    if (selectedCategory === 'all' && items.length < MAX_ITEMS && properties) {
      properties
        .filter((p) => p.is_featured)
        .forEach((property) => {
          if (items.length >= MAX_ITEMS) return;
          if (property.featured_image) {
            pushImage(property.featured_image, {
              type: 'property',
              title: property.title || 'Featured Property',
              subtitle: property.location || 'Prime Location',
              category: property.category || 'Properties',
            });
          }
        });
    }

    if (selectedCategory === 'all' && items.length < MAX_ITEMS && properties) {
      properties.forEach((property) => {
        if (items.length >= MAX_ITEMS) return;
        if (property.featured_image) {
          pushImage(property.featured_image, {
            type: 'property',
            title: property.title || 'Property Showcase',
            subtitle: property.location || 'Available Now',
            category: property.category || 'Properties',
          });
        }
      });
    }

    if (selectedCategory === 'all' && items.length < MAX_ITEMS && blogPosts) {
      blogPosts.forEach((blog) => {
        if (items.length >= MAX_ITEMS) return;
        if (blog.featured_image) {
          pushImage(blog.featured_image, {
            type: 'blog',
            title: blog.title || 'Latest News',
            subtitle: blog.excerpt || 'Read More',
            category: 'Blog Posts',
          });
        }
      });
    }

    return items;
  }, [properties, blogPosts, heroSlides, galleryImages, galleryCategories, selectedCategory]);

  if (loading) {
    return (
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-light title-font text-stone-800 mb-4">
              Featured Gallery
            </h2>
            <p className="text-stone-600 text-lg max-w-3xl mx-auto">
              Explore our properties and developments across Uganda
            </p>
          </div>
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#81be00]"></div>
            <span className="ml-3 text-stone-600">Loading featured images...</span>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-stone-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
        <div className="text-center">
          <h2 className="text-3xl md:text-4xl font-light title-font text-stone-800 mb-4">
            Featured Gallery
          </h2>
          <p className="text-stone-600 text-lg max-w-3xl mx-auto">
            Explore our properties and developments across Uganda
          </p>
        </div>

        <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_280px]">
          <div>
            {featuredImages.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-stone-600">No featured images in this category yet.</p>
              </div>
            ) : (
              <div className="columns-1 sm:columns-2 gap-4 md:gap-6 space-y-6">
                {featuredImages.map((image, index) => (
                  <div
                    key={index}
                    className="relative overflow-hidden rounded-xl group shadow-lg break-inside-avoid"
                  >
                    <div className="overflow-hidden">
                      <img
                        src={image.url}
                        alt={image.title || `Featured image ${index + 1}`}
                        className="w-full object-cover transition-transform duration-500 hover:scale-110"
                        loading="lazy"
                        onError={(e) => {
                          e.target.src = 'https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?auto=format&fit=crop&w=800&q=80';
                        }}
                      />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="absolute bottom-4 left-4 right-4 text-white transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300 opacity-0 group-hover:opacity-100">
                      <p className="text-sm font-medium">{image.title || 'Featured Property'}</p>
                      <p className="text-xs opacity-80">{image.subtitle || 'Nyinimu Development'}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <aside className="lg:border-l lg:border-stone-200 lg:pl-8 space-y-6">
            <div className="rounded-2xl bg-white shadow-sm border border-stone-100 p-6 space-y-5">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-stone-500 mb-4">
                  Categories
                </p>
                <div className="flex flex-wrap gap-3 lg:flex-col">
                  <button
                    onClick={() => setSelectedCategory('all')}
                    className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${
                      selectedCategory === 'all'
                        ? 'bg-[#81be00] text-stone-900'
                        : 'bg-white text-stone-600 border border-stone-200 hover:text-stone-900'
                    }`}
                  >
                    All Categories
                  </button>
                  {galleryCategoryOptions.map((category) => (
                    <button
                      key={category}
                      onClick={() => setSelectedCategory(category)}
                      className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${
                        selectedCategory === category
                          ? 'bg-[#81be00] text-stone-900'
                          : 'bg-white text-stone-600 border border-stone-200 hover:text-stone-900'
                      }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>
              <div className="pt-4 border-t border-stone-100">
                <p className="text-sm text-stone-600">
                  Select a category to explore curated highlights from our dashboard-managed gallery.
                </p>
              </div>
            </div>
            <div className="rounded-2xl bg-white shadow-sm border border-stone-100 p-6 text-center">
              <p className="text-sm text-stone-600 mb-4">
                Want to see more visuals from each project?
              </p>
              <Link
                to="/gallery"
                className="inline-block bg-[#81be00] text-white px-6 py-2 rounded-full font-semibold hover:bg-[#6ba300] transition-colors"
              >
                View Full Gallery
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
};

export default GallerySection;
